import React, { useEffect } from 'react';
import { getLog, setTab } from '@Actions';
import { useDispatch } from 'react-redux';
import {
	Button,
	getSubscriptions,
	InfoCard,
	useCompanyDateTime,
} from '@innovyze/stylovyze';
import { Grid } from '@mui/material';
import { selectRiskStore } from '@Selectors';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import { fullAccess } from '@innovyze/shared-utils';
import { NoReasultsRisk } from '@Components/RiskDetails/NoResultsRisk.component';
import { useGlobalization } from '@Translations/useGlobalization';

interface Props {
	configId: string;
}

export const RiskLogs = ({ configId }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { formatDate } = useCompanyDateTime();
	useEffect(() => {
		dispatch(setTab('logs'));
	}, []);

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const riskStore = selectRiskStore();

	const _formatDateTime = (time: string) =>
		formatDateTimeWithFormatDate(time, formatDate);

	const logTitle = () => {
		if (!riskStore.isLoading) {
			if (riskStore.log && riskStore.log.calcStatus) {
				if (riskStore.log.calcStatus === 'risk-calc-complete') {
					return `${t(
						'Last Run Completed, Start',
					)}: ${_formatDateTime(
						riskStore.log.startTime,
					)}, End: ${_formatDateTime(riskStore.log.endTime)}`;
				} else if (riskStore.log.calcStatus === 'risk-calc-failed') {
					return `${t('Last Run Failed, Start')}: ${_formatDateTime(
						riskStore.log.startTime,
					)}, End: ${_formatDateTime(riskStore.log.endTime)}`;
				} else if (riskStore.log.calcStatus === 'risk-calc-cancelled') {
					return `${t(
						'Last Run Cancelled, Start',
					)}: ${_formatDateTime(
						riskStore.log.startTime,
					)}, End: ${_formatDateTime(riskStore.log.endTime)}`;
				} else {
					return t('Calculation Run Currently in Progress');
				}
			}
			return t('No Log Available');
		}
		return t('Fetching Log');
	};

	const logBodyErrors = () => {
		const shouldShowErrors =
			!riskStore.isLoading &&
			riskStore.log?.calcStatus === 'risk-calc-failed' &&
			riskStore.log.calcErrors.length > 0;
		if (shouldShowErrors) {
			const width = riskStore.log?.log.length !== 0 ? 6 : 12;

			// Filter out duplicates. We can get duplicates since the errors are reported
			// for every risk calculation lambda
			const errors = Array.from(new Set(riskStore.log?.calcErrors ?? []));

			return (
				<Grid item xs={width}>
					<InfoCard title={'Errors'}>
						<ul>
							{errors.map((name, index) => (
								<li key={index}> {name} </li>
							))}
						</ul>
					</InfoCard>
				</Grid>
			);
		}
	};

	const logBodyLog = () => {
		if (
			!riskStore.isLoading &&
			riskStore.log &&
			riskStore.log.log &&
			riskStore.log.log.length != 0
		) {
			const width = riskStore.log?.calcErrors?.length != 0 ? 6 : 12;

			return (
				<Grid item xs={width}>
					<InfoCard title={'Log'}>
						<ul>
							{riskStore.log?.log?.map((name, index) => (
								<li key={index}> {name} </li>
							))}
						</ul>
					</InfoCard>
				</Grid>
			);
		}
	};

	const refreshLog = () => {
		if (!riskStore.isLoading) dispatch(getLog(configId));
	};

	return (
		<InfoCard
			title={logTitle()}
			className={riskStore.isLoading ? 'wait' : ''}
			titleArea={
				<Grid
					container
					style={{ width: 'fit-content', justifyContent: 'flex-end' }}
					spacing={2}>
					<Grid item>
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								refreshLog();
							}}>
							{t('Refresh')}
						</Button>
					</Grid>
				</Grid>
			}>
			<Grid container spacing={3} direction={'row'}>
				{!riskStore.isLoading &&
				(!riskStore.log || !riskStore.log.calcStatus) ? (
					<NoReasultsRisk
						adminFullAccess={hasFullAccess}
						configId={configId}
					/>
				) : (
					<>
						{riskStore.log ? logBodyErrors() : ''}
						{riskStore.log ? logBodyLog() : ''}
					</>
				)}
			</Grid>
		</InfoCard>
	);
};
